import {
  ACTIVITY_PROFILES,
  ActivityProfile,
  IMAGES,
  JOURNEY_CHAPTERS,
  JourneyChapter,
  Language,
  LocalizedContent,
  ROUTE_OPTIONS,
  TravelGuideItem,
} from './siteContent'

const englishChapters: JourneyChapter[] = [
  {
    id: 'arrival',
    mapLabel: 'Arrival',
    eyebrow: 'M&S Travel CR · Tours & Private Transportation in Costa Rica',
    title: 'You made it. Now breathe.',
    lead: 'Your adventure begins the moment you land. We take care of the road ahead.',
    details: ['M&S Travel CR: Tours & Private Transportation in Costa Rica.', 'Your trusted travel companion in Costa Rica.'],
    image: IMAGES.landingPlane,
    imageAlt: 'Plane arriving over the tropical coast of Costa Rica',
    atmosphere: 'sky',
    align: 'center',
    ctaLabel: 'Begin the journey',
  },
  {
    id: 'private-transfer',
    mapLabel: 'Pickup',
    eyebrow: 'Private transportation',
    title: 'We are here. Everything is ready.',
    lead: 'Book safe, private transportation across Costa Rica—comfortable, punctual and shaped around your route.',
    details: [
      'Travel through Costa Rica safely, comfortably and on time.',
      'Share your route details and receive a tailored quote and booking link by email within two business hours during office hours.',
      'Our private transportation service keeps your travel day calm, comfortable and easy.',
    ],
    highlights: ['Private transfer', 'Safe', 'Comfortable', 'On time'],
    image: IMAGES.airportPickup,
    imageAlt: 'Private driver waiting for travelers beside a vehicle',
    atmosphere: 'terminal',
    ctaLabel: 'Request my transfer',
    ctaMessage: 'Hi, I would like a quote for private transportation in Costa Rica.',
  },
  {
    id: 'routes',
    mapLabel: 'Routes',
    eyebrow: 'Costa Rica, coast to coast',
    title: 'Your route has no limits.',
    lead: 'We connect your arrival with the landscapes, towns and experiences you came to discover.',
    details: ['M&S Travel offers safe, private transportation throughout Costa Rica.', 'Explore routes between airports, central cities, coastal regions, rural destinations and the Caribbean.'],
    highlights: ['Air connections & central cities', 'Guanacaste & North Pacific', 'Key natural & rural destinations', 'Central Pacific', 'Caribbean & Atlantic region'],
    image: IMAGES.privateRoutes,
    imageAlt: 'Private vehicle traveling on a road through Costa Rican landscapes',
    atmosphere: 'road',
    align: 'center',
    ctaLabel: 'Plan my route',
    ctaMessage: 'Hi, I would like help planning a private transportation route in Costa Rica.',
  },
  {
    id: 'our-story',
    mapLabel: 'Our story',
    eyebrow: 'But first · let us introduce ourselves',
    title: 'It is not only about arriving. It is about feeling cared for along the way.',
    lead: 'The moment you step off the plane, M&S Travel is already thinking about your road ahead. We were born in the heart of La Fortuna, inspired by Arenal Volcano and a desire to connect people with Costa Rica’s treasures.',
    details: [
      'With more than 18 years of experience, our founder Elfrin Morales learned that what matters most is not only reaching a destination, but enjoying the journey fully.',
      'That is why we turn every transfer and tour into an unforgettable experience.',
      'Our purpose is to be your travel partner in Costa Rica with safe, punctual and comfortable tours and transportation.',
      'We focus on personal attention and real human connection so every traveler leaves with memories that last.',
    ],
    image: IMAGES.personalized,
    imageAlt: 'Travelers sharing a moment in a Costa Rican landscape',
    atmosphere: 'calm',
    align: 'center',
  },
  {
    id: 'explore',
    mapLabel: 'Explore',
    eyebrow: 'Explore with us',
    title: 'Adventure feels better when it is shared.',
    lead: 'Choose from experiences full of nature, fun and adrenaline for couples, friends and families.',
    details: ['Every experience can begin with M&S private transportation: you choose the pace, and we take care of the journey.'],
    highlights: ['Couples', 'Friends', 'Families', 'Adventure', 'Nature'],
    image: IMAGES.privateTransport,
    imageAlt: 'Road between rainforest and mountains in Costa Rica',
    atmosphere: 'road',
    align: 'center',
  },
  {
    id: 'sibaeli',
    mapLabel: 'Sibaeli',
    eyebrow: 'Sibaeli Chocolate Tour',
    title: 'Cacao holds a story.',
    lead: 'Step into the delicious world of chocolate: discover its history, how it is made and flavors shaped by aroma, tradition and place.',
    details: [
      'The Sibaeli family is a family-run company dedicated to producing healthy gourmet cacao products.',
      'They designed a visit that lets you experience the full process behind their artisan chocolate.',
      'At the end, taste traditional and ancestral chocolate drinks and discover their varied cacao products.',
      'With M&S Travel, private transportation takes you to this authentic and delicious Sibaeli Chocolate Tour.',
    ],
    highlights: ['Sibaeli family', 'Artisan chocolate', 'Traditional & ancestral drinks', 'Gourmet cacao products'],
    image: IMAGES.chocolate,
    imageAlt: 'Cacao and artisan chocolate from the Sibaeli Chocolate Tour',
    atmosphere: 'calm',
    ctaLabel: 'Ask about Sibaeli',
    ctaMessage: 'Hi, I would like to ask about the Sibaeli Chocolate Tour and private transportation.',
  },
  {
    id: 'mistico',
    mapLabel: 'Mistico',
    eyebrow: 'Mistico Park · Hanging Bridges',
    title: 'The forest does not end where the sky begins.',
    lead: 'Take in extraordinary views and the thrill of hanging bridges inside a richly biodiverse ecosystem.',
    details: [
      'Mistico Park is a magical place of nature and wildlife, with a memorable hanging-bridge experience.',
      'It is a paradise for birdwatchers, from colorful toucans to tiny hummingbirds that fill the forest with movement.',
      'On the night tour, see the forest come alive with frogs, insects and reptiles in a vibrant nocturnal setting.',
      'M&S Travel can take you there with private transportation designed around your day.',
    ],
    highlights: ['Hanging bridges', 'Birdwatching', 'Night tour', 'Flora & fauna'],
    image: IMAGES.mistico,
    imageAlt: 'Hanging bridge in the misty forest of Mistico Park',
    atmosphere: 'rainforest',
    ctaLabel: 'Discover Mistico',
    ctaMessage: 'Hi, I would like to ask about Mistico Park and private transportation.',
  },
  {
    id: 'mundo-aventura',
    mapLabel: 'Mundo Aventura',
    eyebrow: 'Arenal Mundo Aventura',
    title: 'Arenal is meant to be lived from within.',
    lead: 'An ecological park for adrenaline, nature and culture in the presence of majestic Arenal Volcano.',
    details: [
      'Enjoy nature expeditions, cultural experiences, zipline, horseback riding and night walks.',
      'Ride forest trails on horseback, take in volcano views and explore the rainforest in an authentic way.',
      'Cross the tropical forest by zipline with sweeping views of the volcano and the landscape around La Fortuna.',
      'At night, witness vibrant flora and fauna—and add rappelling for an even bolder way to meet the landscape.',
      'Reserve private transportation with M&S Travel for a comfortable, safe arrival.',
    ],
    highlights: ['Horseback riding', 'Zipline', 'Night tour', 'Rappelling', 'Cultural experiences'],
    image: IMAGES.mundoAventura,
    imageAlt: 'Horseback riding above the rainforest with Arenal Volcano in the distance',
    atmosphere: 'rainforest',
    ctaLabel: 'Experience Mundo Aventura',
    ctaMessage: 'Hi, I would like a quote for Arenal Mundo Aventura and private transportation.',
  },
  {
    id: 'atv',
    mapLabel: 'ATV',
    eyebrow: 'Arenal Adventure ATV',
    title: 'Mud, rivers and pure adventure.',
    lead: 'A four-wheel adventure through remarkable landscapes, surrounded by nature and off-road fun.',
    details: [
      'Arenal Adventure ATV leads you through trails, rivers and mud while looking after your safety at every turn.',
      'Their modern all-terrain fleet is maintained for enjoyment, safety and comfort.',
      'Ask about schedules and get ready for an unforgettable ride near Arenal Volcano.',
      'With M&S Travel, you arrive comfortably and safely—ready to relax on the road and meet the adventure ahead.',
    ],
    highlights: ['ATV tours', 'Trails', 'Rivers', 'Mud', 'Modern fleet'],
    image: IMAGES.atv,
    imageAlt: 'ATV riding through a muddy trail in the Arenal rainforest',
    atmosphere: 'dusk',
    ctaLabel: 'I want to ride ATV',
    ctaMessage: 'Hi, I would like to ask about Arenal Adventure ATV and private transportation.',
  },
  {
    id: 'sloths',
    mapLabel: "Sloth's Territory",
    eyebrow: "Sloth's Territory",
    title: 'The forest moves slowly.',
    lead: 'A 13-hectare natural sanctuary with a vibrant ecosystem made for slowing down and looking closer.',
    details: [
      'Visit Sloth’s Territory with M&S Travel for birdwatching and close observation of two- and three-toed sloths in their natural habitat.',
      'Spot toucans and hummingbirds while walking nature trails with expert guides.',
      'Ask about day and night tour schedules and the details to consider before your visit.',
      'M&S Travel takes care of your comfortable, safe and peaceful ride there.',
    ],
    highlights: ['13 hectares', 'Two- & three-toed sloths', 'Birdwatching', 'Toucans & hummingbirds', 'Day & night tours'],
    image: IMAGES.sloth,
    imageAlt: 'Sloth in its natural habitat at Sloth’s Territory',
    atmosphere: 'rainforest',
    ctaLabel: 'Visit Sloth’s Territory',
    ctaMessage: 'Hi, I would like to ask about Sloth’s Territory and private transportation.',
  },
  {
    id: 'wave',
    mapLabel: 'Wave',
    eyebrow: 'Wave Expeditions',
    title: 'Choose your own mix of adrenaline.',
    lead: 'Wave Expeditions offers high-energy activities to turn your Costa Rica trip into a real adventure.',
    details: [
      'Explore the different Wave Expedition tour combinations. Message us for details and guidance on each activity.',
      'M&S Travel can coordinate private transportation so you can choose the adventure combination that fits you best.',
    ],
    highlights: ['Rafting + ATV', 'Rafting + canyoning', 'Rafting + chocolate', 'Rafting + hanging bridges', 'Rafting + sloth tour', 'Rafting + zipline'],
    image: IMAGES.waveCombos,
    imageAlt: 'White-water rafting and rainforest adventure in Costa Rica',
    atmosphere: 'dusk',
    align: 'center',
    ctaLabel: 'Ask about Wave combos',
    ctaMessage: 'Hi, I would like more details about Wave Expeditions combinations.',
  },
  {
    id: 'arenal-rafting',
    mapLabel: 'Rafting',
    eyebrow: 'Arenal Rafting',
    title: 'The river does not wait.',
    lead: 'In the heart of La Fortuna, ride exciting rapids surrounded by tropical rainforest and remarkable landscapes.',
    details: [
      'For adrenaline and nature in one place, Arenal Rafting combines action-filled rivers with canyoning in the heart of La Fortuna.',
      'Feel the energy, nature and joy of taking on the river—an unforgettable part of a Costa Rica visit.',
      'Add canyoning to descend natural obstacles, explore the forest and meet La Fortuna’s wilder side.',
      'M&S Travel gets you there comfortably and safely so you can focus on the adventure.',
    ],
    highlights: ['Rafting', 'Canyoning', 'Rapids', 'Tropical rainforest', 'La Fortuna'],
    image: IMAGES.canyoning,
    imageAlt: 'Canyoning beside a rainforest waterfall in La Fortuna',
    atmosphere: 'dusk',
    ctaLabel: 'I want this adventure',
    ctaMessage: 'Hi, I would like to ask about Arenal Rafting, canyoning and private transportation.',
  },
  {
    id: 'contact',
    mapLabel: 'Contact',
    eyebrow: 'Get in touch',
    title: 'Your next story begins with a message.',
    lead: 'Whether you have a question or need help shaping your trip, M&S Travel is here to make the experience feel easy from the first step.',
    details: [
      'Email: infomystravel@gmail.com.',
      'Phone: +506 6095 3758.',
      'Based in Alajuela and La Fortuna, Costa Rica.',
      'Request private transportation and receive a tailored quote and booking link by email within two business hours during office hours.',
    ],
    highlights: ['Tours', 'Private transportation', 'Personalized quote', 'Local care in La Fortuna'],
    image: IMAGES.closingSunset,
    imageAlt: 'Sunset over Arenal Volcano in Costa Rica',
    atmosphere: 'sunset',
    align: 'center',
    ctaLabel: 'Talk to M&S Travel',
    ctaMessage: 'Hi M&S Travel, I would like more information and a personalized quote.',
  },
  {
    id: 'feedback',
    mapLabel: 'Your feedback',
    eyebrow: 'Customer care',
    title: 'Every trip teaches us something.',
    lead: 'At M&S Travel, we work every day to offer exceptional private transportation. Your feedback helps us keep improving.',
    details: [
      'We invite you to share your experience through our customer service survey.',
      'Your comments help us learn, grow and keep creating safer, more comfortable journeys for every traveler.',
      'M&S Travel, your trusted transportation in Costa Rica.',
      'Based in Alajuela and La Fortuna, Costa Rica.',
    ],
    image: IMAGES.arenalNight,
    imageAlt: 'Red-eyed tree frog in the Costa Rican night forest',
    atmosphere: 'dusk',
    align: 'center',
    ctaLabel: 'Ask about the survey',
    ctaMessage: 'Hi M&S Travel, I would like to complete the customer service survey.',
  },
]

const englishTravelGuide: TravelGuideItem[] = [
  {
    id: 'arrival',
    label: 'When you land',
    eyebrow: 'Arrival made easy',
    title: 'From the gate to your first deep breath.',
    intro: 'Juan Santamaría International Airport (SJO) is the usual arrival point for travelers exploring La Fortuna, the Pacific and Guanacaste.',
    tips: [
      { title: 'Meet your driver', detail: 'Confirm your pickup details before departure. M&S Travel can arrange a private, door-to-door welcome for your route.' },
      { title: 'Stay connected', detail: 'Airport Wi-Fi is useful for your first message home. An eSIM or local SIM gives you flexibility once you are on the road.' },
      { title: 'Arrive at your pace', detail: 'Costa Rican roads reward unhurried plans. Leave time for traffic, weather, viewpoints and a proper first stop.' },
    ],
  },
  {
    id: 'getting-around',
    label: 'Getting around',
    eyebrow: 'Road confidence',
    title: 'The distance is part of the adventure.',
    intro: 'Costa Rica looks compact on a map, but mountain roads, rain and scenic stops change how a day feels. Private transportation lets the journey work around you.',
    tips: [
      { title: 'Plan by travel rhythm', detail: 'Choose one main region at a time instead of rushing from coast to coast. You will see more by moving less.' },
      { title: 'Let someone else drive', detail: 'A private driver removes navigation, parking and long-drive stress—especially after an international flight.' },
      { title: 'Keep days flexible', detail: 'Weather and road conditions can shift. M&S Travel can help create a route that feels comfortable, not rushed.' },
    ],
  },
  {
    id: 'before-you-go',
    label: 'Before you go',
    eyebrow: 'Pack with confidence',
    title: 'Pack light. Be ready for everything.',
    intro: 'Costa Rica can move from warm sun to tropical rain in one day, especially around Arenal and the rainforest.',
    tips: [
      { title: 'Weather changes fast', detail: 'Light rain protection, breathable clothing, a refillable bottle and comfortable closed-toe shoes cover most travel days.' },
      { title: 'Cards and cash', detail: 'Cards are widely accepted at many hotels, restaurants and tour operators. Keep a small amount of colones for small purchases and tips.' },
      { title: 'Protect your essentials', detail: 'Use sunscreen, insect repellent and a waterproof pouch for a phone or camera on wet adventures.' },
    ],
  },
  {
    id: 'peace-of-mind',
    label: 'Peace of mind',
    eyebrow: 'Local travel care',
    title: 'A calm plan makes a better trip.',
    intro: 'Costa Rica is welcoming and ready for travelers. Like anywhere new, a little preparation keeps your attention where it belongs: on the experience.',
    tips: [
      { title: 'Use trusted partners', detail: 'Choose confirmed transportation and established experiences. Clear answers before you book are part of good service.' },
      { title: 'Keep valuables close', detail: 'Bring only what you need on excursions and use hotel safes for passports and extra cash whenever available.' },
      { title: 'Have someone to contact', detail: 'Save M&S Travel in WhatsApp before you land. A real local contact can make a change of plans feel simple.' },
    ],
  },
]

const englishRouteOptions = [
  { id: 'la-fortuna', region: 'La Fortuna & Arenal', driveTime: 'about 3 hours', note: 'Volcano landscapes, hot springs, rainforest and the heart of M&S Travel.' },
  { id: 'guanacaste', region: 'Guanacaste', driveTime: 'about 4–5 hours', note: 'Pacific beaches, dry tropical forest and a slower, coast-first rhythm.' },
  { id: 'pacific', region: 'Central Pacific', driveTime: 'about 2.5–3.5 hours', note: 'Easy access to beaches, wildlife and ocean sunsets.' },
  { id: 'caribbean', region: 'Caribbean', driveTime: 'about 4–5 hours', note: 'A distinct coast, rich culture and a completely different Costa Rica.' },
]

const englishActivityProfiles: Record<string, ActivityProfile> = {
  sibaeli: { duration: 'Half-day rhythm', difficulty: 'Easy & sensory', idealFor: 'Food lovers & families' },
  mistico: { duration: 'Half or full day', difficulty: 'Easy to moderate', idealFor: 'Nature lovers & photographers' },
  'mundo-aventura': { duration: 'Choose your pace', difficulty: 'Flexible adventure', idealFor: 'Active travelers & groups' },
  atv: { duration: 'Half-day adrenaline', difficulty: 'High energy', idealFor: 'Adventure seekers' },
  sloths: { duration: 'Slow & observant', difficulty: 'Easy walking', idealFor: 'Families & wildlife lovers' },
  wave: { duration: 'Build your own combo', difficulty: 'Choose your level', idealFor: 'Friends & thrill seekers' },
  'arenal-rafting': { duration: 'Adventure day', difficulty: 'Active', idealFor: 'River lovers & bold travelers' },
}

export const EN_CONTENT: LocalizedContent = {
  language: 'en',
  htmlLang: 'en',
  tagline: 'Your trusted travel companion',
  navigation: {
    homeLabel: 'M&S Travel — return to the beginning',
    menuOpenLabel: 'Open journey map',
    menuCloseLabel: 'Close journey map',
    mapLabel: 'Journey map',
    companionLabel: 'Talk to your travel companion',
    switchLabel: 'SPA',
    switchPath: '/',
    switchAriaLabel: 'View the Spanish version',
  },
  journey: {
    startLabel: 'Begin the journey',
    detailsLabel: 'See what to expect',
    hideDetailsLabel: 'Hide trip details',
    highlightsLabel: 'Experience highlights',
    progressLabel: 'Journey progress',
    scrollHint: 'Swipe inside this panel to see more',
  },
  concierge: {
    triggerEyebrow: 'Your travel companion',
    triggerLabel: 'Plan my Costa Rica trip',
    eyebrow: 'M&S Travel concierge',
    title: 'Your trip is already in good hands.',
    intro: 'Use this practical guide before departure, when you land and whenever you need a local answer.',
    closeLabel: 'Close travel companion',
    askLabel: 'Ask M&S Travel',
    whatsappMessage: 'Hi M&S Travel, I have a question about my Costa Rica trip.',
  },
  planner: {
    eyebrow: 'Private route planner',
    title: 'Where do you want to go first?',
    regionsLabel: 'Costa Rica regions',
    airportLabel: 'Juan Santamaría Airport',
    driveLabel: 'Typical drive',
    disclaimer: 'Drive times are planning estimates and may change with weather, road conditions and stops. M&S Travel confirms the best route for your arrival.',
  },
  activity: {
    ariaLabel: 'Experience briefing',
    paceLabel: 'Pace',
    feelLabel: 'Feel',
    bestForLabel: 'Best for',
    careLabel: 'M&S care:',
    careText: 'private transportation and the right timing can be coordinated around this experience.',
  },
  whatsapp: {
    ariaLabel: 'Message M&S Travel on WhatsApp',
    message: 'Hi M&S Travel, I need help planning my Costa Rica trip.',
  },
  navItems: [
    { id: 'home', label: 'Home', scene: 0 },
    { id: 'transport', label: 'Transportation', scene: 1 },
    { id: 'experiences', label: 'Experiences', scene: 5 },
    { id: 'story', label: 'Our story', scene: 3 },
    { id: 'contact', label: 'Contact', scene: 13 },
  ],
  chapters: englishChapters,
  travelGuide: englishTravelGuide,
  routeOptions: englishRouteOptions,
  activityProfiles: englishActivityProfiles,
}

export const EN_CHAPTERS = englishChapters
export const EN_TRAVEL_GUIDE = englishTravelGuide
export const EN_ROUTE_OPTIONS = englishRouteOptions
export const EN_ACTIVITY_PROFILES = englishActivityProfiles
export const EN_LANGUAGE: Language = 'en'
export const EN_REFERENCE_IMAGES = JOURNEY_CHAPTERS.map((chapter) => chapter.image)
export const EN_REFERENCE_ROUTES = ROUTE_OPTIONS
export const EN_REFERENCE_PROFILES = ACTIVITY_PROFILES
